import { useRef, useState, type KeyboardEvent } from 'react';
import { SendIcon, StopIcon } from '@/components/icons';
import { ART } from '@/art';

export interface ComposerProps {
  onSend: (text: string) => void | Promise<void>;
  /** When provided and `disabled` is true, a Stop button is shown that calls onAbort. */
  onAbort?: () => void;
  disabled?: boolean;
}

export function Composer({ onSend, onAbort, disabled }: ComposerProps): JSX.Element {
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  // once the input has had focus, leaving it plays the advisor's climb back down (Peek)
  const [hasPeeked, setHasPeeked] = useState(false);

  async function submit() {
    const trimmed = text.trim();
    if (!trimmed || disabled) return;
    setText('');
    await onSend(trimmed);
    textareaRef.current?.focus();
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void submit();
    }
  }

  const canSend = text.trim().length > 0 && !disabled;
  const showStop = disabled && typeof onAbort === 'function';

  return (
    // Outer wrapper is full-width (aligns with the full-width scroll
    // area above); inner is the centered max-800 column so the composer
    // lines up with the message content.
    <div className="tw-py-3">
      <div className="tw-max-w-[800px] tw-mx-auto tw-px-2">
      <form
        onFocus={() => setHasPeeked(true)}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        // Floating pill input — a resting soft shadow + light border
        // lifts it off the (transparent) background so it reads as a
        // distinct affordance even when not focused; focus deepens the
        // border + ring.
        className={`cal-composer${hasPeeked ? ' has-peeked' : ''} tw-relative tw-flex tw-items-end tw-gap-2 tw-bg-cal-surface tw-border tw-border-cal-border tw-rounded-[26px] tw-px-4 tw-py-2.5 tw-shadow-[0_1px_8px_rgba(0,0,0,0.27)] focus-within:tw-border-cal-green-500 focus-within:tw-shadow-[0_2px_12px_rgba(0,0,0,0.30)] focus-within:tw-ring-2 focus-within:tw-ring-cal-green-500/15 tw-transition-all`}
      >
        <Peek />
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
          disabled={disabled}
          placeholder="Précisez votre question…"
          aria-label="Message à envoyer à l'assistant"
          className="tw-flex-1 tw-resize-none tw-border-0 tw-outline-none tw-bg-transparent tw-text-[14.5px] tw-leading-relaxed tw-text-cal-ink placeholder:tw-text-cal-ink-muted tw-py-1.5 tw-min-h-[24px] tw-max-h-[200px]"
        />
        {showStop ? (
          <button
            type="button"
            onClick={onAbort}
            aria-label="Arrêter la génération de la réponse"
            className="tw-flex tw-items-center tw-justify-center tw-w-9 tw-h-9 tw-rounded-lg tw-bg-red-500 tw-text-white hover:tw-bg-red-600 tw-transition-colors tw-shadow-sm"
          >
            <StopIcon width={14} height={14} />
          </button>
        ) : (
          <button
            type="submit"
            disabled={!canSend}
            aria-label="Envoyer le message"
            className="tw-flex tw-items-center tw-justify-center tw-w-9 tw-h-9 tw-rounded-xl tw-bg-cal-green-500 tw-text-white hover:tw-bg-cal-green-700 tw-transition-colors disabled:tw-bg-cal-bg-soft disabled:tw-text-cal-ink-muted disabled:tw-cursor-not-allowed"
          >
            <SendIcon width={14} height={14} />
          </button>
        )}
      </form>
      <p className="tw-mt-2 tw-text-[11px] tw-text-cal-ink-muted tw-text-center tw-leading-snug">
        Assistant virtuel Calebasse peut commettre des erreurs. Il est recommandé de vérifier les
        informations importantes.
      </p>
      </div>
    </div>
  );
}

/**
 * While the user types (the input has focus), the advisor climbs up from behind the input's
 * top edge and looks around; leaving it, he climbs back down — all CSS, on
 * .cal-composer:focus-within / .has-peeked. Decorative only.
 */
function Peek(): JSX.Element {
  return (
    <span className="cal-peek" aria-hidden="true">
      <img className="cal-peek-hand is-left" src={ART.peekLeftHand} alt="" />
      <span className="cal-peek-head">
        <span className="cal-peek-rise">
          <img src={ART.peekHead} alt="" />
        </span>
      </span>
      {/* the head again, cut down to its mic: that one stays in front of the edge */}
      <span className="cal-peek-head is-mic">
        <span className="cal-peek-rise">
          <img src={ART.peekHead} alt="" />
        </span>
      </span>
      <img className="cal-peek-hand is-right" src={ART.peekRightHand} alt="" />
    </span>
  );
}
